import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Mail,
  Globe,
  Clock,
  GraduationCap,
  Bot,
  CreditCard,
  Wrench,
  ShieldCheck,
  ArrowUpRight,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { LEGAL, LEGAL_META } from "../i18n/legal";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const CATEGORY_ICONS = [GraduationCap, Bot, CreditCard, Wrench];

export default function ContactPage() {
  const { lang } = useLanguage();
  const L = LEGAL[lang] || LEGAL.en || LEGAL.es;
  const page = L.contact;
  const common = L.common;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white antialiased" data-testid="legal-page-contact">
      <Navbar />

      <section className="relative w-full overflow-hidden pb-12 pt-32 md:pt-40">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(168,85,247,0.16),transparent_55%)]" />
        <div className="absolute inset-x-0 top-0 mx-auto h-px max-w-5xl bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage: "radial-gradient(circle at 50% 0%, black, transparent 70%)",
          }}
        />

        <div className="relative mx-auto max-w-5xl px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-white/50 transition-colors hover:text-white"
              data-testid="contact-back-home"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {common.back_home}
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-purple-400/30 bg-white/[0.03] px-3 py-1.5 backdrop-blur"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-purple-400 shadow-[0_0_8px_rgba(168,85,247,0.8)]" />
            <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-purple-300 md:text-xs">
              {LEGAL_META.entity}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-5 font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl"
            data-testid="contact-title"
          >
            {page.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-4 max-w-3xl text-base leading-relaxed text-white/55 md:text-lg"
          >
            {page.subtitle}
          </motion.p>
        </div>
      </section>

      <section className="relative w-full bg-black pb-32">
        <div className="mx-auto max-w-5xl px-6 md:px-12">
          {/* Main contact card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.04] via-white/[0.02] to-transparent p-8 backdrop-blur-xl md:p-12"
          >
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              <div className="md:col-span-2">
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-purple-300/80">
                  {page.email_label}
                </p>
                <a
                  href={`mailto:${LEGAL_META.contact_email}`}
                  className="mt-3 inline-flex items-baseline gap-2 font-display text-2xl font-black tracking-tight text-white transition-colors hover:text-purple-200 md:text-4xl"
                  data-testid="contact-email-link"
                >
                  {LEGAL_META.contact_email}
                  <ArrowUpRight className="h-5 w-5" />
                </a>
                <a
                  href={`mailto:${LEGAL_META.contact_email}`}
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(255,255,255,0.3)]"
                  data-testid="contact-cta-button"
                >
                  <Mail className="h-4 w-4" />
                  {page.cta_label}
                </a>
              </div>

              <div className="space-y-5 border-t border-white/10 pt-6 md:border-l md:border-t-0 md:pl-8 md:pt-0">
                <div>
                  <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-white/50">
                    <Clock className="h-3.5 w-3.5" />
                    {page.response_label}
                  </div>
                  <p className="mt-2 text-sm text-white/80">{page.response_time}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-white/50">
                    <Globe className="h-3.5 w-3.5" />
                    {page.website_label}
                  </div>
                  <p className="mt-2 text-sm text-white/80">{LEGAL_META.website}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Categories grid */}
          <div className="mt-14">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/40">
              {page.categories_title}
            </p>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {page.categories.map((c, i) => {
                const Icon = CATEGORY_ICONS[i] || GraduationCap;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.5, delay: 0.05 * i }}
                    className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-md transition-all hover:border-purple-400/30 hover:bg-white/[0.05] hover:shadow-[0_0_30px_rgba(168,85,247,0.12)]"
                    data-testid={`contact-category-${i}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-purple-400/30 bg-purple-400/10">
                        <Icon className="h-4 w-4 text-purple-300" />
                      </div>
                      <h3 className="font-display text-lg font-bold text-white">{c.title}</h3>
                    </div>
                    <p className="mt-4 text-sm leading-relaxed text-white/55">{c.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Educational disclaimer */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
            className="mt-14 flex items-start gap-3 rounded-2xl border border-amber-400/25 bg-amber-400/[0.04] p-5"
            data-testid="contact-disclaimer"
          >
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-amber-400/30 bg-black/40">
              <ShieldCheck className="h-4 w-4 text-amber-300" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-amber-300">
                {LEGAL_META.entity}
              </p>
              <p className="mt-1 text-sm text-white/65">{page.disclaimer}</p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
